import React, { memo, useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Alert,
} from "reactstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useEventDetail } from "../../hooks/Dashboard/Schedule/useEventDetail";
import DayOfWeekSelector from "./DayOfWeekSelector";
import { HUMAN_TIMEZONES } from "../../constants";
import TimezoneSelect from "../TimeZoneDropDown/TimezoneSelect";

// Custom Radio Button Component
const CustomRadioButton = ({ name, value, checked, onChange, label }) => {
  return (
    <div
      className="custom-radio-button"
      style={{
        cursor: "pointer",
        position: "relative",
        overflow: "visible",
        display: "table",
        height: "auto",
        width: "auto",
        whiteSpace: "nowrap",
        marginRight: "15px",
        textTransform: "uppercase",
      }}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        style={{
          position: "absolute",
          cursor: "inherit",
          pointerEvents: "all",
          opacity: 0,
          width: "100%",
          height: "100%",
          zIndex: 2,
          left: 0,
          boxSizing: "border-box",
          padding: 0,
          margin: 0,
        }}
      />
      <div
        style={{
          display: "flex",
          width: "100%",
          height: "100%",
          alignItems: "center",
        }}
      >
        <div
          style={{
            transition: "all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms",
            position: "relative",
            display: "block",
            flexShrink: 0,
            width: "18px",
            marginRight: "8px",
            marginLeft: "0px",
            height: "18px",
          }}
        >
          <div style={{ position: "relative", width: "18px", height: "18px" }}>
            <svg
              viewBox="0 0 24 24"
              style={{
                display: "inline-block",
                color: "rgb(58, 62, 82)",
                fill: checked ? "rgb(81, 127, 164)" : "rgb(189, 189, 189)",
                height: "18px",
                width: "18px",
                userSelect: "none",
                transition: "all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms",
                position: "absolute",
                opacity: 1,
                transform: "scale(1)",
              }}
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"></path>
              {checked && (
                <circle cx="12" cy="12" r="5" fill="rgb(81, 127, 164)" />
              )}
            </svg>
          </div>
        </div>
        <label
          style={{
            position: "relative",
            display: "block",
            lineHeight: "18px",
            color: "rgb(117, 117, 117)",
            fontFamily: '"Work Sans", sans-serif',
            textTransform: "none",
            fontSize: "14px",
            fontWeight: checked ? "600" : "400",
          }}
        >
          {label}
        </label>
      </div>
    </div>
  );
};

// Custom Select Input Component
const CustomSelectInput = ({ value, onChange, placeholder, options, name }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (option) => {
    onChange(name, option.value);
    setIsOpen(false);
  };

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div
      style={{
        fontSize: "14px",
        lineHeight: "20px",
        width: "100%",
        height: "40px",
        display: "inline-block",
        position: "relative",
        backgroundColor: "transparent",
        fontFamily: '"Work Sans", sans-serif',
        transition: "height 200ms cubic-bezier(0.23, 1, 0.32, 1) 0ms",
        cursor: "pointer",
        borderRadius: "4px",
        border: "1px solid #e0e0e0",
      }}
      onClick={() => setIsOpen(!isOpen)}
    >
      <div
        style={{
          display: "block",
          font: "inherit",
          height: "100%",
          outline: "none",
          position: "relative",
          transition: "all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms",
          padding: "0px",
          width: "100%",
          border: "none",
          backgroundColor: "rgba(0, 0, 0, 0)",
          color: "rgb(58, 62, 82)",
          cursor: "inherit",
          WebkitTapHighlightColor: "rgba(0, 0, 0, 0)",
        }}
      >
        <div
          style={{
            cursor: "pointer",
            height: "100%",
            position: "relative",
            width: "100%",
          }}
        >
          <div
            style={{
              color: "rgb(58, 62, 82)",
              height: "40px",
              lineHeight: "40px",
              overflow: "hidden",
              opacity: 1,
              position: "relative",
              paddingLeft: "12px",
              paddingRight: "40px",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: "14px",
            }}
          >
            {selectedOption?.label || placeholder}
          </div>
          <button
            tabIndex="0"
            type="button"
            style={{
              border: "10px",
              boxSizing: "border-box",
              display: "inline-block",
              fontFamily: '"Work Sans", sans-serif',
              WebkitTapHighlightColor: "rgba(0, 0, 0, 0)",
              cursor: "pointer",
              textDecoration: "none",
              margin: "0px",
              padding: "8px",
              outline: "none",
              fontSize: "0px",
              fontWeight: "inherit",
              position: "absolute",
              zIndex: 1,
              overflow: "visible",
              transition: "all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms",
              width: "40px",
              height: "40px",
              fill: "rgb(58, 62, 82)",
              right: "0px",
              top: "0px",
              background: "none",
            }}
          >
            <div>
              <svg
                viewBox="0 0 24 24"
                style={{
                  display: "inline-block",
                  color: "rgb(58, 62, 82)",
                  fill: "inherit",
                  height: "20px",
                  width: "20px",
                  userSelect: "none",
                  transition: "all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms",
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                <path d="M7 10l5 5 5-5z"></path>
              </svg>
            </div>
          </button>
        </div>
      </div>
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderTop: "none",
            zIndex: 10,
            maxHeight: "200px",
            overflowY: "auto",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            borderRadius: "0 0 4px 4px",
          }}
        >
          {options.map((option) => (
            <div
              key={option.value}
              style={{
                padding: "10px 12px",
                cursor: "pointer",
                backgroundColor:
                  value === option.value
                    ? "#f5f5f5"
                    : "transparent",
                borderBottom: "1px solid #f0f0f0",
                fontSize: "14px",
                transition: "background-color 0.2s",
              }}
              onClick={() => handleSelect(option)}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#f9f9f9"}
              onMouseLeave={(e) => e.target.style.backgroundColor = 
                value === option.value ? "#f5f5f5" : "transparent"}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const EventDetail = memo(
  ({ isOpen, onClose, refetchEvents, onShowSnackbar }) => {
    const {
      values,
      setFieldValue,
      isSubmitting,
      handleSubmit,
      showError,
      localError,
      setShowError,
      setLocalError,
      isEditing,
      handleDelete,
      tagOptions,
      errors,
      touched,
      submitCount,
      resetForm,
      handleChange,
      handleBlur,
    } = useEventDetail({
      isOpen,
      onClose,
      refetchEvents,
      onShowSnackbar,
    });

    const formatErrorMessage = (error) => {
      if (typeof error === "string") {
        return error;
      }
      if (error && typeof error === "object") {
        return error.message || error.error || JSON.stringify(error);
      }
      return "An error occurred";
    };

    const getTimezoneLabel = (value) => {
      const entry = Object.entries(HUMAN_TIMEZONES).find(
        ([key, val]) => val === value
      );
      return entry ? entry[0] : null;
    };

    // Options for custom select components
    const dayOfMonthOptions = Array.from({ length: 31 }, (_, i) => ({
      label: 'Day ' + (i + 1),
      value: i + 1,
    }));
    const weekOptions = [
      { label: 'First', value: 1 },
      { label: 'Second', value: 2 },
      { label: 'Third', value: 3 },
      { label: 'Fourth', value: 4 },
      { label: 'Fifth', value: 5 }
    ];
    const weekDayOptions = [
      { label: 'Sunday', value: 'SU' },
      { label: 'Monday', value: 'MO' },
      { label: 'Tuesday', value: 'TU' },
      { label: 'Wednesday', value: 'WE' },
      { label: 'Thursday', value: 'TH' },
      { label: 'Friday', value: 'FR' },
      { label: 'Saturday', value: 'SA' }
    ];
   

    // Handle recurring type change
    const handleRecurringTypeChange = (type) => {
      setFieldValue("recurringType", type);
      
      // Reset dependent fields when changing recurring type
      if (type === "weekly") {
        setFieldValue("monthlyOption", "");
        setFieldValue("monthlyDays", "");
        setFieldValue("monthlyWeek", "");
        setFieldValue("monthlyweekDay", "");
      } else if (type === "monthly") {
        setFieldValue("days", []);
      } else {
        setFieldValue("days", []);
        setFieldValue("monthlyOption", "");
        setFieldValue("monthlyDays", "");
        setFieldValue("monthlyWeek", "");
        setFieldValue("monthlyweekDay", "");
      }
    };

    // Handle monthly option change
    const handleMonthlyOptionChange = (option) => {
      setFieldValue("monthlyOption", option);
      
      // Reset the other option's fields
      if (option === "daywise") {
        setFieldValue("monthlyWeek", "");
        setFieldValue("monthlyweekDay", "");
      } else {
        setFieldValue("monthlyDays", "");
      }
    };

    return (
      <Modal
        isOpen={isOpen}
        toggle={onClose}
        size="lg"
        style={{ maxWidth: "600px", width: "100%", fontFamily: '"Work Sans", sans-serif' }}
        className="event-detail-modal"
      >
        <ModalHeader 
          toggle={onClose} 
          className="px-4 py-3"
          style={{ 
            borderBottom: "1px solid #eaeaea",
            fontSize: "18px",
            fontWeight: "600"
          }}
        >
          {isEditing ? "Edit Event" : "Create New Event"}
        </ModalHeader>
        <ModalBody
          style={{
            maxHeight: "65vh",
            overflowY: "auto",
            padding: "0 24px",
            fontSize: "14px",
          }}
        >
          {showError && localError && (
            <Alert
              color="danger"
              className="mt-3 mb-3 py-2"
              toggle={() => {
                setShowError(false);
                setLocalError(null);
              }}
              style={{ fontSize: "13px" }}
            >
              {localError}
            </Alert>
          )}
          <form onSubmit={handleSubmit} id="event-form" className="pb-4">
            <Row>
              <Col md={12}>
                <FormGroup className="mb-3">
                  <Label for="title" className="form-label fw-semibold">
                    Title *
                  </Label>
                  <Input
                    name="title"
                    id="title"
                    placeholder="Enter event title"
                    className="form-control"
                    style={{ 
                      minHeight: "40px", 
                      fontSize: "14px",
                      borderRadius: "4px"
                    }}
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={errors.title && touched.title}
                  />
                  {errors.title && touched.title && (
                    <div className="text-danger mt-1 small" style={{ fontSize: "12px" }}>
                      {errors.title}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup className="mb-3">
                  <Label for="description" className="form-label fw-semibold">
                    Description
                  </Label>
                  <Input
                    type="textarea"
                    name="description"
                    id="description"
                    className="form-control"
                    rows="3"
                    placeholder="Enter event description"
                    style={{ 
                      resize: "none", 
                      fontSize: "14px",
                      borderRadius: "4px"
                    }}
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={errors.description && touched.description}
                  />
                  {errors.description && touched.description && (
                    <div className="text-danger mt-1 small" style={{ fontSize: "12px" }}>
                      {errors.description}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup className="mb-3">
                  <Label className="form-label fw-semibold">Show Genres</Label>
                  <Select
                    isMulti
                    options={tagOptions}
                    value={tagOptions.filter((option) =>
                      values.tags.includes(option.value)
                    )}
                    onChange={(selectedOptions) =>
                      setFieldValue(
                        "tags",
                        selectedOptions
                          ? selectedOptions.map((option) => option.value)
                          : []
                      )
                    }
                    placeholder="Select genres..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    invalid={errors.tags && touched.tags}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "40px",
                        fontSize: "14px",
                        borderRadius: "4px"
                      }),
                      placeholder: (base) => ({
                        ...base,
                        fontSize: "14px"
                      }),
                    }}
                  />
                  {errors.tags && touched.tags && (
                    <div className="text-danger mt-1 small" style={{ fontSize: "12px" }}>
                      {errors.tags}
                    </div>
                  )}
                </FormGroup>
                
              </Col>
              <Col md={12}>
                <FormGroup className="mb-3">
                  <Label className="form-label fw-semibold">DJ List</Label>
                  <CreatableSelect
                    isMulti
                    options={values.hosts.map((host) => ({
                      value: host,
                      label: host,
                    }))}
                    value={values.hosts.map((host) => ({
                      value: host,
                      label: host,
                    }))}
                    onChange={(selectedOptions) =>
                      setFieldValue(
                        "hosts",
                        selectedOptions
                          ? selectedOptions.map((option) => option.value)
                          : []
                      )
                    }
                    placeholder="Add DJs..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    invalid={errors.hosts && touched.hosts}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "40px",
                        fontSize: "14px",
                        borderRadius: "4px"
                      }),
                      placeholder: (base) => ({
                        ...base,
                        fontSize: "14px"
                      }),
                    }}
                  />
                  <small className="text-muted mt-1 d-block" style={{ fontSize: "12px" }}>
                    Type in the name of the DJs and then press "tab" or
                    "enter" to confirm
                  </small>
                  {errors.hosts && touched.hosts && (
                    <div className="text-danger mt-1 small" style={{ fontSize: "12px" }}>
                      {errors.hosts}
                    </div>
                  )}
                </FormGroup>
                
              </Col>
            </Row>
            
            <div className="border-top pt-3 mb-3"></div>
            
            <Row >
              <Col md={12} className="d-flex align-items-center pb-2 gap-3">
                <div className="fw-semibold">Time</div> 
                <FormGroup check className="mt-1">
                  <Label check className="form-check-label" style={{ fontSize: "14px" }}>
                    <Input
                      type="checkbox"
                      name="allDay"
                      className="form-check-input"
                      checked={values.allDay}
                      onChange={(e) => {
                        const newAllDay = e.target.checked;
                        setFieldValue("allDay", newAllDay);
                        if (newAllDay) {
                          setFieldValue("startTime", "");
                          setFieldValue("duration", 1440);
                        } else {
                          setFieldValue("startTime", "");
                          setFieldValue("duration", "");
                        }
                      }}
                    />{" "}
                    All day
                  </Label>
                </FormGroup>
              </Col>
              <Col md={12} className="pb-3">
                <TimezoneSelect
                  label="Timezone"
                  id="timezone"
                  name="tz"
                  value={getTimezoneLabel(values.tz)}
                  onChange={(e) => {
                    const newTimezone = HUMAN_TIMEZONES[e.target.value];
                    setFieldValue("tz", newTimezone);
                  }}
                />
              </Col>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label for="startTime" className="form-label fw-semibold">
                    Start Time
                  </Label>
                  <Input
                    type="time"
                    name="startTime"
                    id="startTime"
                    className="form-control"
                    disabled={values.allDay}
                    value={values.startTime}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={{ 
                      fontSize: "14px",
                      borderRadius: "4px",
                      height: "40px"
                    }}
                    invalid={errors.startTime && touched.startTime && !values.allDay}
                  />
                  {errors.startTime && touched.startTime && !values.allDay && (
                    <div className="text-danger mt-1 small" style={{ fontSize: "12px" }}>
                      {errors.startTime}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                {!values.allDay && <FormGroup className="mb-3">
                  <Label for="duration" className="form-label fw-semibold">
                    Duration (minutes)
                  </Label>
                  <Input
                    type="number"
                    name="duration"
                    id="duration"
                    className="form-control"
                    min="15"
                    step="15"
                    disabled={values.allDay}
                    value={values.duration}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={{ 
                      fontSize: "14px",
                      borderRadius: "4px",
                      height: "40px"
                    }}
                    invalid={errors.duration && touched.duration && !values.allDay}
                  />
                  {errors.duration && touched.duration && !values.allDay && (
                    <div className="text-danger mt-1 small" style={{ fontSize: "12px" }}>
                      {errors.duration}
                    </div>
                  )}
                </FormGroup> }
              </Col>
            </Row>
            
            <div className="border-top pt-3 mb-3"></div>
            
            <Row className="align-items-center">
              <Col md={4} className="d-flex align-items-center pb-2 gap-3">
                <div className="fw-semibold">Date</div>
                <FormGroup check className="mt-1">
                  <Label check className="form-check-label" style={{ fontSize: "14px" }}>
                    <Input
                      type="checkbox"
                      name="recurring"
                      className="form-check-input"
                      checked={values.recurring}
                      onChange={(e) => {
                        setFieldValue("recurring", e.target.checked);
                      }}
                    />{" "}
                    Recurring
                  </Label>
                </FormGroup>
              </Col>
              {values.recurring && (
                <Col md={4} className="d-flex align-items-center pb-2 gap-3">
                  <FormGroup check className="form-check-label mt-1">
                    <Label check style={{ fontSize: "14px" }}>
                      <Input
                        type="checkbox"
                        name="haslongduration"
                        checked={values.haslongduration}
                        onChange={(e) =>
                          setFieldValue("haslongduration", e.target.checked)
                        }
                      />{" "}
                      No End Date
                    </Label>
                  </FormGroup>
                </Col>
              )}
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label for="startDate" className="form-label fw-semibold">
                    Start Date
                  </Label>
                  <Input
                    type="date"
                    name="startDate"
                    id="startDate"
                    className="form-control"
                    value={values.startDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={{ 
                      fontSize: "14px",
                      borderRadius: "4px",
                      height: "40px"
                    }}
                    invalid={errors.startDate && touched.startDate}
                  />
                  {errors.startDate && touched.startDate && (
                    <div className="text-danger mt-1 small" style={{ fontSize: "12px" }}>
                      {errors.startDate}
                    </div>
                  )}
                </FormGroup>
              </Col>
              {values.recurring && !values.haslongduration && (
                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label for="endDate" className="form-label fw-semibold">
                      End Date
                    </Label>
                    <Input
                      type="date"
                      name="endDate"
                      id="endDate"
                      className="form-control"
                      disabled={values.haslongduration}
                      value={values.endDate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      style={{ 
                        fontSize: "14px",
                        borderRadius: "4px",
                        height: "40px"
                      }}
                      invalid={errors.endDate && touched.endDate}
                    />
                    {errors.endDate && touched.endDate && (
                      <div className="text-danger mt-1 small" style={{ fontSize: "12px" }}>
                        {errors.endDate}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              )}
            </Row>
            {values.recurring && (
              <>
                <div className="border-top pt-3 mb-3"></div>
                
                <Row>
                  <Col md={12}>
                    <div
                      style={{
                        display: "flex",
                        marginBottom: "16px",
                      }}
                    >
                      <CustomRadioButton
                        name="recurringType"
                        value="daily"
                        checked={values.recurringType === "daily"}
                        onChange={() => handleRecurringTypeChange("daily")}
                        label="Daily"
                      />
                      <CustomRadioButton
                        name="recurringType"
                        value="weekly"
                        checked={values.recurringType === "weekly"}
                        onChange={() => handleRecurringTypeChange("weekly")}
                        label="Weekly"
                      />
                      <CustomRadioButton
                        name="recurringType"
                        value="monthly"
                        checked={values.recurringType === "monthly"}
                        onChange={() => handleRecurringTypeChange("monthly")}
                        label="Monthly"
                      />
                    </div>
                  </Col>
                </Row>
                
                {values.recurringType === "weekly" && (
                  <Row className="mb-3">
                    <Col md={12}>
                      <DayOfWeekSelector
                        value={values.days}
                        onChange={(v) => setFieldValue("days", v)}
                      />
                    </Col>
                  </Row>
                )}
                {values.recurringType === "monthly" && (
                  <>
                  <Row className="mb-3">
                  <Col md={12}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "16px",
                      }}
                    >
                      <span style={{ marginRight: "10px", fontSize: "14px" }}>
                        Repeats every
                      </span>
                      <div
                        style={{
                          width: "70px",
                          marginRight: "10px",
                        }}
                      >
                        <Input
                          type="number"
                          value={values.repeatsEvery || 1}
                          onChange={(e) =>
                            setFieldValue("repeatsEvery", parseInt(e.target.value) || 1)
                          }
                          min="1"
                          style={{ 
                            textAlign: "center",
                            fontSize: "14px",
                            height: "40px",
                            borderRadius: "4px"
                          }}
                        />
                      </div>
                      <span style={{ fontSize: "14px" }}>Month(s)</span>
                    </div>
                  </Col>
                </Row>
                
                    <Row className="mb-3">
                      <Col md={12}>
                        <div
                          style={{
                            display: "flex",
                            marginBottom: "16px",
                          }}
                        >
                          <CustomRadioButton
                            name="monthlyOption"
                            value="daywise"
                            checked={values.monthlyOption === "daywise"}
                            onChange={() => handleMonthlyOptionChange("daywise")}
                            label="Day of the month"
                          />
                          <CustomRadioButton
                            name="monthlyOption"
                            value="weekwise"
                            checked={values.monthlyOption === "weekwise"}
                            onChange={() => handleMonthlyOptionChange("weekwise")}
                            label="Day of the week"
                          />
                        </div>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={12}>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center",
                          }}
                        >
                          {values.monthlyOption === "daywise" ? (
                            <div style={{ width: "280px" }}>
                              <CustomSelectInput
                                placeholder="Select Day of the Month"
                                options={dayOfMonthOptions}
                                value={values.monthlyDays}
                                onChange={setFieldValue}
                                name="monthlyDays"
                              />
                            </div>
                          ) : (
                            <>
                              <div style={{ width: "140px" }}>
                                <CustomSelectInput
                                  placeholder="Week"
                                  options={weekOptions}
                                  value={values.monthlyWeek}
                                  onChange={setFieldValue}
                                  name="monthlyWeek"
                                />
                              </div>
                              <div style={{ width: "140px" }}>
                                <CustomSelectInput
                                  placeholder="Week Day"
                                  options={weekDayOptions}
                                  value={values.monthlyweekDay}
                                  onChange={setFieldValue}
                                  name="monthlyweekDay"
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </>
                )}
              </>
            )}
          </form>
        </ModalBody>
        <ModalFooter 
          className="px-4 py-3"
          style={{ 
            borderTop: "1px solid #eaeaea",
          }}
        >
          <Button 
            color="secondary" 
            onClick={onClose} 
            className="px-4"
            style={{
              fontSize: "14px",
              borderRadius: "4px",
              padding: "8px 16px"
            }}
          >
            Cancel
          </Button>
          {isEditing && (
            <Button
              color="danger"
              onClick={handleDelete}
              disabled={isSubmitting}
              className="px-4"
              style={{
                fontSize: "14px",
                borderRadius: "4px",
                padding: "8px 16px"
              }}
            >
              Delete
            </Button>
          )}
          <Button
            color="primary"
            type="submit"
            form="event-form"
            disabled={isSubmitting}
            className="px-4"
            style={{
              fontSize: "14px",
              borderRadius: "4px",
              padding: "8px 16px",
              fontWeight: "600"
            }}
          >
            {isSubmitting
              ? "Saving..."
              : isEditing
                ? "Update Event"
                : "Create Event"}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
);
EventDetail.displayName = "EventDetail";

export default EventDetail;